import {
  ArrowLeft,
  FileSearch,
  Mail,
  MessageCircle,
  MessageSquare,
  ReceiptText,
  Shield,
  UserPlus,
} from 'lucide-react'
import { Helmet } from 'react-helmet'
import { Link, useLocation, useNavigate, useParams } from 'react-router'

import { Button } from '@/components/ui/button'
import { Skeleton, SkeletonRows } from '@/components/ui/skeleton'
import { ToggleTabs } from '@/components/ui/ToggleTabs'
import { InsuranceApplicationSource } from '../../../../models/insuranceApplication'
import { useInternalApplication } from '../../../../models/internalApplication'
import { useOrganisation } from '../../../../models/user'
import { EmailsTab } from '../Customers/EmailsTab'
import { InsuranceTab } from '../Customers/InsuranceTab'
import { LossRunsTab } from '../Customers/LossRunsTab'
import { ProspectsTab } from '../Customers/ProspectsTab'
import { QuoteTab } from '../Customers/QuoteTab'
import { SmsMessagesTab } from '../Customers/SmsMessagesTab'

const DEFAULT_TAB = 'application'

export function ApplicationDetail() {
  const { id } = useParams<{ id: string }>()
  const { application, isLoading: appLoading } = useInternalApplication(id)
  const { organisation, isLoading: orgLoading } = useOrganisation(application?.orgId)
  const location = useLocation()
  const navigate = useNavigate()

  const isEmailSource = application?.source === InsuranceApplicationSource.EMAIL
  const isSmsSource = application?.source === InsuranceApplicationSource.SMS
  const defaultTab = isEmailSource ? 'emails' : isSmsSource ? 'sms' : DEFAULT_TAB
  const currentTab = location.hash.slice(1) || defaultTab

  function onTabChange(value: string) {
    void navigate(
      { search: location.search, hash: value === defaultTab ? '' : value },
      { replace: true, preventScrollReset: true }
    )
  }

  if (appLoading || orgLoading) {
    return (
      <div className="flex flex-col gap-5">
        <Skeleton className="h-8 w-56" />
        <Skeleton className="h-4 w-40" />
        <SkeletonRows rows={6} />
      </div>
    )
  }

  if (!application || !organisation) {
    return <div className="mt-10 text-center">Application not found</div>
  }

  return (
    <>
      <Helmet>
        <title>Application Details</title>
      </Helmet>
      <div className="flex min-h-0 flex-1 flex-col gap-5">
        <div className="-ml-3 flex items-center">
          <Button variant="link" asChild>
            <Link to="/insure/internal/applications">
              <ArrowLeft className="size-4" />
              Back to applications
            </Link>
          </Button>
        </div>

        <div>
          <div className="text-2xl font-semibold">{application.name}</div>
          <Link
            to={`/insure/internal/customers/${application.orgId}`}
            className="text-muted-foreground block w-fit text-sm underline-offset-2 hover:underline"
          >
            {application.orgName}
          </Link>
        </div>

        <ToggleTabs
          value={currentTab}
          onValueChange={onTabChange}
          className="flex min-h-0 w-full flex-1 flex-col"
          items={[
            { value: 'emails', icon: <Mail />, label: 'Emails', hidden: !isEmailSource },
            { value: 'sms', icon: <MessageCircle />, label: 'SMS', hidden: !isSmsSource },
            { value: 'application', icon: <MessageSquare />, label: 'Application' },
            { value: 'quotes', icon: <ReceiptText />, label: 'Quotes' },
            { value: 'insurance', icon: <Shield />, label: 'Insurance' },
            { value: 'loss-runs', icon: <FileSearch />, label: 'Loss Runs' },
            { value: 'prospects', icon: <UserPlus />, label: 'Prospects', hidden: !application.hasProspects },
          ]}
        >
          {currentTab === 'emails' && isEmailSource && <EmailsTab applicationId={application.id} />}
          {currentTab === 'sms' && isSmsSource && <SmsMessagesTab applicationId={application.id} />}
          {currentTab === 'application' && <QuoteTab orgId={organisation.id} />}
          {currentTab === 'quotes' && <QuoteTab orgId={organisation.id} quotesPage />}
          {currentTab === 'insurance' && <InsuranceTab org={organisation} />}
          {currentTab === 'loss-runs' && <LossRunsTab orgId={organisation.id} />}
          {currentTab === 'prospects' && application.hasProspects && (
            <ProspectsTab applicationId={application.id} />
          )}
        </ToggleTabs>
      </div>
    </>
  )
}
